import { PDFDocument, type PDFFont, rgb, StandardFonts } from "pdf-lib"

import { cuerpoParaAncho } from "./ajuste"
import { type Caja, POSICIONAMIENTO } from "./geometria"
import { CABECERA, PIE } from "./medidas"
import type { DatosPosicionamiento } from "./tipos"

/**
 * El PDF de una Solicitud de Posicionamiento: la página ORIGINAL del SENIAT con lo
 * escrito encima.
 *
 * ⚠ **Es el archivo que se imprime Y el que se guarda en el registro.** El navegador
 * no deja capturar lo que manda a la impresora, así que la única forma de que lo
 * guardado sea exactamente lo que salió en papel es que el papel salga de aquí. La
 * hoja de la pantalla sigue siendo donde se escribe; esto es su versión para
 * ventanilla.
 *
 * ⚠ **El fondo es el PDF que publica la aduana, no nuestra reproducción.** Rótulos,
 * cajas y logotipo son los suyos, vectoriales, sin un punto de diferencia. Lo único
 * que se añade es el texto tecleado, en las mismas coordenadas que usa la pantalla
 * (`geometria.ts` y `medidas.ts`): una sola fuente de posiciones para los dos.
 *
 * ⚠ **La letra encoge con la MISMA regla que en pantalla** (`cuerpoParaAncho`):
 * mismo mínimo, mismo margen. Lo que cambia es quién mide —aquí las métricas de
 * Helvetica, allí `canvas`—, porque cada dibujante tiene que medir su propia fuente.
 */

/** Altura de la mayúscula de Helvetica, en em. Es lo que centra el texto en la casilla. */
const ALTURA_MAYUSCULA = 0.718

/** Relleno lateral de un campo, en pt. El mismo que en pantalla (`piezas.tsx`). */
const RELLENO = 3

/**
 * La tinta de lo tecleado: el azul marino del formato.
 *
 * ⚠ Es el valor de `--plantilla-oscura` en `globales.css`, repetido porque `pdf-lib`
 * no lee CSS. Si se cambia uno, se cambia el otro: es el color medido sobre el PDF
 * del SENIAT, así que en la práctica no cambia.
 */
const TINTA = rgb(0x25 / 255, 0x2e / 255, 0x6d / 255)

export type Colocacion = { x: number; y: number; cuerpo: number; cabe: boolean }

/**
 * Dónde y a qué cuerpo va un texto dentro de una casilla, en el sistema de `pdf-lib`.
 *
 * ⚠ **`pdf-lib` mide desde ABAJO; el formato, desde arriba.** Las coordenadas de la
 * aduana se sacaron con el origen arriba a la izquierda; el PDF pone el origen abajo.
 * `techo` es el borde superior de la página en el sistema del PDF —y NO la altura a
 * secas: la `MediaBox` de este documento empieza en y = 7,83 y no en cero, así que
 * restar desde la altura dejaría todo el texto 7,83 pt corrido—.
 *
 * Es pura para poder probar la cuenta sin montar un PDF.
 */
export function colocar(
  anchoACien: number,
  caja: Caja,
  base: number,
  techo: number,
  centrado: boolean,
): Colocacion {
  const { cuerpo, cabe } = cuerpoParaAncho(anchoACien, caja.w - RELLENO * 2, base)
  const ancho = (anchoACien * cuerpo) / 100
  const x = centrado ? caja.x + (caja.w - ancho) / 2 : caja.x + RELLENO
  // La línea base, para que la mayúscula quede centrada en vertical en la casilla.
  const y = techo - (caja.y + caja.h / 2 + (ALTURA_MAYUSCULA * cuerpo) / 2)
  return { x, y, cuerpo, cabe }
}

/**
 * Lo tecleado, sin los caracteres que Helvetica no sabe escribir.
 *
 * ⚠ **Sin esto, un solo carácter raro tumba la impresión entera.** Las fuentes
 * estándar del PDF sólo codifican WinAnsi —el español cabe entero, con sus tildes, la
 * eñe y los signos de apertura—, y `pdf-lib` LANZA ante lo demás: un emoji pegado en
 * la mercancía dejaría a la persona sin poder imprimir y sin saber por qué. Se cambia
 * por «?», que en papel se ve y se corrige.
 */
function escribible(fuente: PDFFont, texto: string): string {
  let salida = ""
  for (const letra of texto) {
    try {
      fuente.encodeText(letra)
      salida += letra
    } catch {
      salida += "?"
    }
  }
  return salida
}

/**
 * Rellena la página original con los datos. Devuelve los bytes del PDF final.
 *
 * ⚠ **Se copia a un `Uint8Array<ArrayBuffer>` al salir.** `pdf-lib` declara un
 * `Uint8Array` a secas, que para TypeScript puede vivir sobre un `SharedArrayBuffer`, y
 * un `Blob` no admite eso. La copia son 170 kB; el tipo honesto evita un `as` en cada
 * sitio que lo convierte en archivo.
 */
export async function rellenarPosicionamiento(
  plantilla: ArrayBuffer | Uint8Array,
  datos: DatosPosicionamiento,
): Promise<Uint8Array<ArrayBuffer>> {
  const pdf = await PDFDocument.load(plantilla)
  const pagina = pdf.getPage(0)
  const fuente = await pdf.embedFont(StandardFonts.Helvetica)
  const negrita = await pdf.embedFont(StandardFonts.HelveticaBold)
  const caja = pagina.getMediaBox()
  const techo = caja.y + caja.height

  const texto = (valor: string, donde: Caja, base = 10, centrado = false) => {
    const limpio = escribible(fuente, valor.trim())
    if (limpio === "") return
    const sitio = colocar(fuente.widthOfTextAtSize(limpio, 100), donde, base, techo, centrado)
    pagina.drawText(limpio, {
      x: sitio.x,
      y: sitio.y,
      size: sitio.cuerpo,
      font: fuente,
      color: TINTA,
    })
  }

  // La equis de una casilla marcada: en negrita y al 80 % del alto, como en pantalla.
  const marca = (marcada: boolean, donde: Caja) => {
    if (!marcada) return
    const cuerpo = donde.h * 0.8
    const ancho = negrita.widthOfTextAtSize("X", cuerpo)
    pagina.drawText("X", {
      x: donde.x + (donde.w - ancho) / 2,
      y: techo - (donde.y + donde.h / 2 + (ALTURA_MAYUSCULA * cuerpo) / 2),
      size: cuerpo,
      font: negrita,
      color: TINTA,
    })
  }

  // --- Cabecera ------------------------------------------------------------------
  for (const casilla of CABECERA.casillas) {
    marca(datos.regimenes[casilla.clave], { ...casilla.caja, ...CABECERA.casilla })
  }
  for (const parte of ["dia", "mes", "anio"] as const) {
    texto(datos.fecha_solicitud[parte], CABECERA.fecha[parte].caja, 10, true)
  }

  // --- Cuerpo --------------------------------------------------------------------
  const g = POSICIONAMIENTO
  texto(datos.dua, g.dua)
  texto(datos.bl, g.bl)
  texto(datos.agencia, g.agencia)
  texto(datos.registro, g.registro)
  for (const parte of ["dia", "mes", "anio"] as const) {
    texto(datos.fecha_reconocimiento[parte], g.reconocimiento[parte], 10, true)
  }
  texto(datos.funcionario, g.funcionario)
  texto(datos.color, g.color)
  texto(datos.almacen, g.almacen)
  texto(datos.codigo, g.codigo)
  texto(datos.mercancia, g.mercancia)
  marca(datos.contenedor, g.contenedor)
  marca(datos.carga_suelta, g.cargaSuelta)
  texto(datos.cantidad, g.cantidad, 10, true)
  for (const indice of [0, 1, 2] as const) {
    texto(datos.siglas[indice], g.siglas[indice])
  }

  // --- Pie: el responsable. La firma no se escribe: se firma a mano. ---------------
  const pie = PIE.POSICIONAMIENTO
  PIE.lineas.forEach((linea, indice) => {
    if (linea.campo === null) return
    const y = pie.y + PIE.salto * (indice + 1) + PIE.primera
    // La misma caja que el campo de pantalla: 1 pt por encima y 15 de alto.
    texto(datos.responsable[linea.campo], { x: pie.x, y: y - 1, w: PIE.renglon, h: 15 }, 11.2)
  })

  return new Uint8Array(await pdf.save())
}
